// Ported from Octo.Mobile@79a7c69 src/api/dataApi.ts
import axios from 'axios';
import { apiClient } from './http/client';
import { ENDPOINTS } from './endpoints';
import type { SelectResponse, UpsertRequest, DeleteRequest, ProcessResult, ChangePasswordRequest, UserProfileImageResponse } from '@/shared/types/api';
import type { ExecuteStoredProcedureRequest, StoredProcedureResponse, SqlParameterModel, SurveyResponseRequest } from '@/shared/types/data';
import type { SystemSurvey } from '@/shared/types/system';
import { normalizeQuestionType, questionTypeToInt } from '@/shared/types/system';

/**
 * Normalize a SqlParameterModel from API camelCase to TypeScript PascalCase.
 * ASP.NET Core default JSON serialization uses camelCase, but our TS types
 * use PascalCase (Name, IntValue, etc.) matching the C# property names.
 */
function normalizeSqlParam(raw: Record<string, unknown>): SqlParameterModel {
  return {
    Name:          (raw.Name ?? raw.name ?? '') as string,
    ValueKind:     (raw.ValueKind ?? raw.valueKind) as SqlParameterModel['ValueKind'],
    IntValue:      (raw.IntValue ?? raw.intValue) as number | null | undefined,
    BoolValue:     (raw.BoolValue ?? raw.boolValue) as boolean | null | undefined,
    DateTimeValue: (raw.DateTimeValue ?? raw.dateTimeValue) as string | null | undefined,
    StringValue:   (raw.StringValue ?? raw.stringValue) as string | null | undefined,
    DecimalValue:  (raw.DecimalValue ?? raw.decimalValue) as number | null | undefined,
    GuidValue:     (raw.GuidValue ?? raw.guidValue) as string | null | undefined,
  };
}

/**
 * SP/trigger hata mesajını "Mesaj :" ayırıcısından message ve detail olarak ayır.
 * DataManipulationService.cs'deki parsing mantığının aynısı.
 */
function splitSqlMessage(fullMessage: string): { message: string; detail: string | null } {
  // "Mesaj" veya "Message" ayırıcısını regex ile bul (tab/boşluk farkını tolere et)
  const match = fullMessage.match(/(Mesaj|Message)\s*:\s*/i);
  if (match && match.index != null) {
    const detailPart = fullMessage.substring(0, match.index).trim();
    const messagePart = fullMessage.substring(match.index + match[0].length).trim();
    return {
      message: messagePart || fullMessage,
      detail: detailPart || null,
    };
  }
  return { message: fullMessage, detail: null };
}

export const dataApi = {
  // Select with filter
  selectMasterFilter: (
    objectId: string,
    userId: string,
    filterId?: string,
    filter?: string,
    maxRecords?: number
  ): Promise<SelectResponse> =>
    apiClient.get<SelectResponse>(
      ENDPOINTS.SELECT_MASTER_FILTER(objectId, userId, filterId, filter, maxRecords)
    ),

  /**
   * SP-data-source ekran verisi: SP çalıştırılır, ilk result set + OUTPUT parametreleri döner.
   * Standart token'lar (@sssip/@UserId/@RolId) sunucuda tamamlanır; istemcinin gönderdiği
   * aynı adlı değerler ezilir.
   */
  selectMasterFilterSp: async (
    objectId: string,
    userId: string,
    parameters: SqlParameterModel[]
  ): Promise<SelectResponse> => {
    const raw = await apiClient.post<SelectResponse>(
      ENDPOINTS.SELECT_MASTER_FILTER_SP(objectId, userId),
      parameters
    );
    if (raw?.outputParameters?.length) {
      raw.outputParameters = raw.outputParameters.map((p) =>
        normalizeSqlParam(p as unknown as Record<string, unknown>)
      );
    }
    if (raw && !raw.success && raw.message && !raw.detail) {
      const { message, detail } = splitSqlMessage(raw.message);
      raw.message = message;
      raw.detail = detail;
    }
    return raw;
  },

  // Yönlendirici SP mi? (OUTPUT @ResultDataSourceObjectId)
  spHasRedirectOutput: async (objectId: string): Promise<boolean> => {
    try {
      const res = await apiClient.get<boolean>(ENDPOINTS.SP_HAS_REDIRECT_OUTPUT(objectId));
      return res === true;
    } catch {
      return false;
    }
  },

  // Select lookup data
  selectLookup: (
    fieldId: string,
    userId: string,
    ignoreWhereClause?: boolean
  ): Promise<SelectResponse> =>
    apiClient.get<SelectResponse>(
      ENDPOINTS.SELECT_LOOKUP(fieldId, userId, ignoreWhereClause)
    ),

  // Insert or update
  upsert: (request: UpsertRequest): Promise<SelectResponse> =>
    apiClient.post<SelectResponse>(ENDPOINTS.UPSERT, request),

  // Delete — backend ProcessResult.ErrorMessage döner; message alanına normalize edilir
  delete: async (request: DeleteRequest): Promise<ProcessResult> => {
    // success:false yanıtı burada işlenir: istemcinin genel "İşlem başarısız." hatasına düşerse
    // sunucunun anlattığı sebep (ör. kaydın nerede kullanıldığı, #486) kayboluyordu.
    const raw = await apiClient.post<ProcessResult>(ENDPOINTS.DELETE, request, { octo: { expectSuccess: false } });
    if (raw && !raw.success && !raw.message && raw.errorMessage) {
      const { message, detail } = splitSqlMessage(raw.errorMessage);
      raw.message = message;
      raw.detail = raw.detail ?? detail;
    }
    return raw;
  },

  // File upload
  uploadFile: (fileTableId: string, fileName: string, fileContentBase64: string): Promise<SelectResponse> =>
    apiClient.post<SelectResponse>(ENDPOINTS.UPLOAD_FILE, {
      FileTableId: fileTableId,
      FileName: fileName,
      FileContent: fileContentBase64,
    }),

  // File download
  getFile: (fileTableId: string, fileName: string): Promise<SelectResponse> =>
    apiClient.get<SelectResponse>(`/api/data/GetFile/${fileTableId}/${encodeURIComponent(fileName)}`),

  // Execute stored procedure
  executeStoredProcedure: async (request: ExecuteStoredProcedureRequest): Promise<StoredProcedureResponse> => {
    const raw = await apiClient.post<StoredProcedureResponse>(ENDPOINTS.EXECUTE_SP, request);
    // Normalize output parameter property names from API camelCase to TS PascalCase
    if (raw.outputParameters?.length) {
      raw.outputParameters = raw.outputParameters.map((p) =>
        normalizeSqlParam(p as unknown as Record<string, unknown>)
      );
    }
    return raw;
  },

  /**
   * Execute scalar — backend `[FromBody] string` bekler: gövde düz JSON string'dir.
   * Sorgu tam olarak gönderilir (SELECT dahil); yanıt { value }.
   */
  executeScalar: async (sql: string): Promise<unknown> => {
    const res = await apiClient.post<{ value: unknown }>(ENDPOINTS.EXECUTE_SCALAR, JSON.stringify(sql), {
      headers: { 'Content-Type': 'application/json' },
    });
    return res?.value ?? null;
  },


  /** Rapor açılış audit'i (web TryWriteReportLog) — hata yutulur */
  writeReportLog: async (req: {
    objectId: string; userId: string | null; userName: string | null;
    reportActionId: string; reportId: string | null; reportName: string;
    parameters: string; isDialog: boolean; durationMs: number; success: boolean; errorMessage: string | null;
  }): Promise<void> => {
    try {
      await apiClient.post(ENDPOINTS.WRITE_REPORT_LOG, req);
    } catch {
      // audit isteğe bağlı
    }
  },

  /** Parametreli skaler sorgu (AutoCode sayacı vb.) — yanıt { value } */
  executeScalarWithParams: async (sql: string, parameters: SqlParameterModel[]): Promise<unknown> => {
    const res = await apiClient.post<{ value: unknown }>(ENDPOINTS.EXECUTE_SCALAR_WITH_PARAMS, { Sql: sql, Parameters: parameters });
    return res?.value ?? null;
  },

  /** Profil fotoğrafı güncelle (web ProfileImageProcessor: 256px kare, ≤48KB hedef; sunucu sınırı 256KB) */
  updateUserProfileImage: (userId: string, imageBase64: string | null): Promise<SelectResponse> =>
    apiClient.post<SelectResponse>(ENDPOINTS.UPDATE_USER_PROFILE_IMAGE, { UserId: userId, ImageBytes: imageBase64 }),


  // Profil fotoğrafı (LoginResponse'tan kaldırıldı, ayrı çekilir)
  getUserProfileImage: async (userId: string): Promise<string | null> => {
    const res = await apiClient.get<UserProfileImageResponse>(ENDPOINTS.USER_PROFILE_IMAGE(userId));
    return res?.imageBytes ?? null;
  },

  // Survey
  getSurvey: async (surveyId: string): Promise<SystemSurvey> => {
    const survey = await apiClient.get<SystemSurvey | null>(ENDPOINTS.GET_SURVEY(surveyId));
    // DAL bulunamayan anket için boş gövde döner; henüz bölümü olmayan anketin Sections'ı null gelir.
    if (!survey || typeof survey !== 'object') throw new Error(`Survey not found: ${surveyId}`);
    survey.sections ??= [];
    // Normalize question types: API may return integers (C# enum default) instead of strings
    for (const section of survey.sections) {
      section.questions ??= [];
      for (const q of section.questions) {
        q.type = normalizeQuestionType(q.type);
        q.options ??= [];
      }
    }
    return survey;
  },

  /** Anket tasarımını kaydeder (Blazor Dal.Data.UpsertSurveyAsync). Soru tipi enum sayısı olarak gider. */
  upsertSurvey: async (survey: SystemSurvey & { updateUserId?: string }): Promise<void> => {
    await apiClient.post(ENDPOINTS.UPSERT_SURVEY, {
      ...survey,
      sections: survey.sections.map((s) => ({
        ...s,
        questions: s.questions.map((q) => ({ ...q, type: questionTypeToInt(q.type) })),
      })),
    });
  },

  submitSurveyResponse: async (request: SurveyResponseRequest): Promise<ProcessResult> => {
    try {
      const result = await apiClient.post<ProcessResult>(ENDPOINTS.SUBMIT_SURVEY, request);
      // Backend başarısız döndüyse mesajı SP tarzı ayır
      if (!result.success && result.message && !result.detail) {
        const { message, detail } = splitSqlMessage(result.message);
        result.message = message;
        result.detail = detail;
      }
      return result;
    } catch (err: unknown) {
      // 400/500 hata durumunda backend response body'den ProcessResult oluştur
      if (axios.isAxiosError(err) && err.response?.data) {
        const d = err.response.data as Record<string, unknown>;
        const rawMessage =
          (d.message as string) || (d.Message as string) ||
          (d.title as string) || (d.Title as string) || 'İşlem başarısız.';
        const rawDetail =
          (d.detail as string) || (d.Detail as string) || null;

        // Backend zaten ayırmışsa (detail doluysa) kullan, yoksa mesajı SP tarzı ayır
        if (rawDetail) {
          return { success: false, message: rawMessage, detail: rawDetail };
        }
        const { message, detail } = splitSqlMessage(rawMessage);
        return { success: false, message, detail };
      }
      throw err;
    }
  },

  /**
   * Şifre değiştirme — /api/system/ChangePassword (merkezî kimlik farkında).
   * Başarısızlıkta backend 400 + { success, message } döner; axios hatası yakalanıp
   * normal sonuç olarak verilir.
   */
  changePassword: async (request: ChangePasswordRequest): Promise<{ success: boolean; message: string | null }> => {
    const body = {
      UserId: request.UserId,
      CurrentPassword: request.CurrentPassword ?? request.OldPassword,
      OldPassword: request.OldPassword ?? request.CurrentPassword,
      NewPassword: request.NewPassword,
    };
    try {
      const res = await apiClient.post<Record<string, unknown>>(ENDPOINTS.SYSTEM_CHANGE_PASSWORD, body);
      return {
        success: !!(res?.success ?? res?.Success),
        message: ((res?.message ?? res?.Message) as string | undefined) ?? null,
      };
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data) {
        const d = err.response.data as Record<string, unknown>;
        const msg = (d.message ?? d.Message ?? d.title ?? d.Title) as string | undefined;
        return { success: false, message: msg ?? 'Şifre değiştirilemedi.' };
      }
      throw err;
    }
  },
};
